import React, { useState } from 'react';
import {
  LayoutDashboard,
  BarChart,
  TrendingDown,
  DollarSign,
  Target,
  Activity
} from 'lucide-react';
import { ResponseDecompositionWaterfall } from './ResponseDecompositionWaterfall';
import { ActualVsPredicted } from './ActualVsPredicted';
import { DecayCurves } from './DecayCurves';
import { ShareOfSpend } from './ShareOfSpend';
import { EffectAndPerformance } from './EffectAndPerformance';
import { BootstrappedMetrics } from './BootstrappedMetrics';

interface VisualizationDashboardProps {
  modelResults?: any;
  title?: string;
}

type TabType =
  | 'overview'
  | 'decomposition'
  | 'adstock_decay'
  | 'spend'
  | 'performance'
  | 'metrics';

interface TabConfig {
  id: TabType;
  label: string;
  icon: React.ReactNode;
  description: string;
}

const tabs: TabConfig[] = [
  {
    id: 'overview',
    label: 'Overview',
    icon: <LayoutDashboard className="w-4 h-4" />,
    description: 'Model fit & channel contributions',
  },
  {
    id: 'decomposition',
    label: 'Decomposition',
    icon: <BarChart className="w-4 h-4" />,
    description: 'Response by predictor',
  },
  {
    id: 'adstock_decay',
    label: 'Adstock & Decay',
    icon: <TrendingDown className="w-4 h-4" />,
    description: 'Media effect decay over time',
  },
  {
    id: 'spend',
    label: 'Budget Allocation',
    icon: <DollarSign className="w-4 h-4" />,
    description: 'Share of spend',
  },
  {
    id: 'performance',
    label: 'Performance',
    icon: <Target className="w-4 h-4" />,
    description: 'ROAS & efficiency',
  },
  {
    id: 'metrics',
    label: 'Uncertainty',
    icon: <Activity className="w-4 h-4" />,
    description: '95% confidence intervals',
  },
];

export const VisualizationDashboard: React.FC<VisualizationDashboardProps> = ({
  modelResults,
  title = 'Channel Contributions & Impact Analysis',
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('overview');

  return (
    <div className="w-full bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="border-b border-slate-200 bg-slate-50 p-6">
        <h2 className="text-xl font-bold text-slate-800 mb-2">{title}</h2>
        <p className="text-slate-600 text-sm">
          Comprehensive visualization suite for understanding channel contributions, impact, and performance metrics.
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-slate-200 bg-white px-6 py-2 overflow-x-auto">
        <div className="flex gap-1 min-w-max">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 ${activeTab === tab.id
                  ? 'bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200 shadow-sm'
                  : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`}
              title={tab.description}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="p-6">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <ActualVsPredicted
              data={modelResults?.actual_vs_predicted}
              nrmse={modelResults?.metrics?.nrmse_val}
              r2={modelResults?.metrics?.r2_val}
              title="Actual vs Predicted Response"
            />
            <ResponseDecompositionWaterfall
              contributions={modelResults?.contributions_by_channel || {}}
              baselineValue={modelResults?.metrics?.baseline_value || 0}
              depVar={modelResults?.config?.dep_var || 'revenue'}
              mediaChannels={modelResults?.media_channels || modelResults?.config?.media_channels || []}
              title="Channel Contributions Decomposition"
            />
          </div>
        )}

        {activeTab === 'decomposition' && (
          <ResponseDecompositionWaterfall
            contributions={modelResults?.contributions_by_channel || {}}
            baselineValue={modelResults?.metrics?.baseline_value || 0}
            depVar={modelResults?.config?.dep_var || 'revenue'}
            mediaChannels={modelResults?.media_channels || modelResults?.config?.media_channels || []}
            title="Response Decomposition by Predictor"
          />
        )}

        {activeTab === 'adstock_decay' && (
          <div className="space-y-6">
            <DecayCurves
              data={modelResults?.decay_curves}
              title="Media Effect Decay Over Time"
            />
            {/* Compact summary of immediate vs carryover */}
            {modelResults?.adstock_decomposition && modelResults.adstock_decomposition.length > 0 && (
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                <h4 className="text-sm font-semibold text-slate-700 mb-3">Immediate vs Carryover Summary</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                  {modelResults.adstock_decomposition.map((item: any) => {
                    const total = item.immediate + item.carryover;
                    const carryoverPct = total > 0 ? ((item.carryover / total) * 100).toFixed(0) : '0';
                    return (
                      <div key={item.channel} className="bg-white rounded-md p-3 border border-slate-200">
                        <div className="text-xs font-medium text-slate-500 truncate">{item.channel}</div>
                        <div className="text-lg font-bold text-indigo-600">{carryoverPct}%</div>
                        <div className="text-xs text-slate-400">carryover effect</div>
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-500 mt-3">
                  Higher carryover % indicates longer-lasting ad effects. Channels with high carryover benefit from sustained investment.
                </p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'spend' && (
          <ShareOfSpend
            data={modelResults?.spend_share}
            title="Share of Total Media Spend by Channel"
            useBarChart={true}
          />
        )}

        {activeTab === 'performance' && (
          <EffectAndPerformance
            data={modelResults?.performance_metrics}
            title="Effect & Performance Metrics by Channel"
          />
        )}

        {activeTab === 'metrics' && (
          <BootstrappedMetrics
            data={modelResults?.bootstrapped_metrics}
            metricName="Impact"
            title="Channel Impact Uncertainty (95% Confidence Intervals)"
          />
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
        <div className="flex items-center justify-between text-sm text-gray-600">
          <div>
            <p>
              <strong>Tab Description:</strong>{' '}
              {tabs.find((t) => t.id === activeTab)?.description}
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-500">
              Use these visualizations to understand your model's performance and channel contributions.
              Click the "Explain" button in any visualization for AI-powered insights.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
